import { ListResponse } from '../list-response';
import { RevenueBySourceTypeResult } from '../metrics';
import type { GenericTimePeriodValuePerColumnItem } from '../../services/metrics/metrics.service';
import type { TimeInterval } from '../../utils';

/**
 * Paginates through an API collection, formatting the results for export.
 *
 * @param selectedColumnsList - The list of columns to include for each Model
 *   item.
 * @param getPage - A function accepting a page number and returning a promise
 *   for a page of Model items.
 * @param getContentItem - A function accepting a Model item and a column name,
 *   and returning a formatted string for that cell in the result content.
 * @param mustStop - A function which returns true if the function should exit
 *   early (do to the export being cancelled, for example).
 * @return A promise for a string double array containing the result content.
 */
export function loadApiExport<Model>(selectedColumnsList: string[],
                                     getPage: (pageNum: number) => Promise<ListResponse<Model>>,
                                     getContentItem: (item: Model, columnName: string) => string,
                                     mustStop: () => boolean): Promise<string[][]> {
  const content = [selectedColumnsList];

  function addResultsToContent(listResponse: ListResponse<Model>) {
    for (const item of listResponse.results) {
      const thisContentRow: string[] = [];
      for (const selectedColumnName of selectedColumnsList) {
        thisContentRow.push(
          getContentItem(item, selectedColumnName)
        );
      }
      content.push(thisContentRow);
    }
  }

  function loadRemainingPages(pageNum: number): Promise<void> {
    if (mustStop()) {
      return Promise.resolve();
    }

    return getPage(pageNum).then(response => {
      addResultsToContent(response);
      if (response.nextPage){
        return loadRemainingPages(pageNum + 1);
      } else {
        return Promise.resolve();
      }
    });
  }

  return loadRemainingPages(1).then(() => {
    return content;
  });
}

export function getRevenueBySourceTypeContent(result: RevenueBySourceTypeResult,
                                              sourceNameById: {[key: number]: string},
                                              sourceIds: number[]): string[][] {
  const headerRow = ['Date'];
  for (const sourceId of sourceIds) {
    headerRow.push(sourceNameById[sourceId]);
  }
  headerRow.push('Total');
  const content = [headerRow];

  const dateKeys = Object.keys(result);
  dateKeys.sort();
  for (const dateKey of dateKeys) {
    const dateRow = [dateKey];
    let dateRowTotal = 0;
    for (const sourceId of sourceIds) {
      const columnValue = result[dateKey][sourceId];
      dateRow.push(columnValue.toString());
      dateRowTotal += columnValue;
    }
    dateRow.push(dateRowTotal.toString());
    content.push(dateRow);
  }

  return content;
}

export function getGenericTimePeriodValuePerColumnContent(result: GenericTimePeriodValuePerColumnItem[],
                                                          timeInterval: TimeInterval,
                                                          sourceNameById: {[key: number]: string},
                                                          sourceIds: number[]): string[][] {
  const headerRow: string[] = [timeInterval.singleIntervalTerm];
  for (const sourceId of sourceIds) {
    headerRow.push(sourceNameById[sourceId])
  }
  headerRow.push('Total');
  const csvContent: string[][] = [headerRow];
  for (const resultItem of result) {
    const resultRow: string[] = [resultItem.time_period_machine];
    let total = 0;
    for (const sourceId of sourceIds) {
      const thisVal = resultItem.value[sourceId];
      resultRow.push(thisVal.toString());
      total += thisVal;
    }
    resultRow.push(total.toString());
    csvContent.push(resultRow);
  }
  return csvContent;
}
